import React, {useEffect, useState} from "react"
import {Typography} from "@mui/material"
import Grid from "@mui/material/Grid2"
import {useDispatch, useSelector} from "react-redux"
import {ApiResponse} from "apisauce"
import api from "../../../Services/api"
import {RootState} from "../../../Redux"
import PaymentActions from '../../../Redux/paymentRedux'
import PaymentHeaders from "./PaymentHeaders"
import PrintReceipt from "../Common/PrintReceipt"
import BackButton from "../Common/BackButton"
import {ServiceCardType} from "../Types"
import {useTranslation} from "react-i18next"
import {getLocale, numberToCurrencyString} from "../../../Utils/helpers"
import selfServiceRedux from "../../../Redux/selfServiceRedux"

const { getCheckIn } = api.create()

interface NexiPayWithCardProps {
    serviceCard: ServiceCardType
    setServiceCard: (serviceCard: ServiceCardType) => void
    setPaymentMade: (paymentMade: boolean) => void
    onBack: () => void
}

export const NexiPayWithCard = ({ serviceCard, setServiceCard, setPaymentMade, onBack }: NexiPayWithCardProps) => {
    const cardPayment = useSelector((state: RootState) => state.payment.cardPayment)
    const cardPaymentCompletedStatus = useSelector((state: RootState) => state.payment.cardPaymentStatus.isCompleted)
    const fetching = useSelector((state: RootState) => state.payment.fetching.payment)
    const [cardPaymentCompleted, setCardPaymentCompleted] = useState<boolean>(cardPaymentCompletedStatus)
    const [printReceipt, setPrintReceipt] = useState<boolean>(false)
    const [paymentInitialized, setPaymentInitialized] = useState<boolean>(false)
    const [abortRequested, setAbortRequested] = useState<boolean>(false)

    const dispatch = useDispatch()
    const { t } = useTranslation()
    const language = getLocale()

    useEffect(() => {
        dispatch(selfServiceRedux.setAllowClose(false))
    }, [dispatch])

    useEffect(() => {
        setCardPaymentCompleted(status => status || cardPaymentCompletedStatus)
    }, [cardPaymentCompletedStatus])

    useEffect(() => {
        if (paymentInitialized && (cardPaymentCompleted || cardPaymentCompletedStatus) && cardPayment?.isPaid) {
            setPrintReceipt(true)
        }
    }, [cardPayment, cardPaymentCompleted, cardPaymentCompletedStatus, paymentInitialized])

    useEffect(() => {
        if (!paymentInitialized && !abortRequested) {
            dispatch(PaymentActions.createCardPayment(serviceCard.token, language))
            setCardPaymentCompleted(false)
            setPaymentInitialized(true)
        }
    }, [dispatch, language, paymentInitialized, abortRequested, serviceCard.token])

    const handleGoBack = () => {
        dispatch(PaymentActions.abortCardPayment())
        setAbortRequested(true)
    }

    useEffect(() => {
        if (abortRequested && (cardPaymentCompleted || cardPaymentCompletedStatus)) {
            dispatch(selfServiceRedux.setAllowClose(true))
            setAbortRequested(false)
            onBack()
        }
    }, [abortRequested, cardPaymentCompleted, cardPaymentCompletedStatus, dispatch, onBack])

    const getStatusMessage = () => {
        if (abortRequested) {
            return t('selfService.paymentAbortRequested')
        }
        if (cardPaymentCompleted || cardPaymentCompletedStatus) {
            if (cardPayment?.isPaid !== undefined) {
                return cardPayment.isPaid ? t('selfService.paymentSucceeded') : t('errors.paymentFailed')
            }
        }
        return t('selfService.followPaymentTerminalInstructions')
    }

    const onPrintDone = async () => {
        const checkInResponse: ApiResponse<any> = await getCheckIn(serviceCard.token)
        if (checkInResponse.ok) {
            const updatedServiceCard = checkInResponse.data as ServiceCardType
            if (updatedServiceCard.sale.remainingPrice.vatIncluded === 0) {
                setServiceCard(updatedServiceCard)
            }
        }
        dispatch(selfServiceRedux.setAllowClose(true))
        setPaymentMade(true)
    }

    return (
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={4} className={'pageMainContainer'}>
            <Grid className="pageMainHeader">
                <PaymentHeaders serviceCard={serviceCard} />
            </Grid>
            <Grid textAlign="center">
                <Typography variant="h5" style={{marginBottom: '1em'}}>
                    {serviceCard.sale.tasks[0]?.basket?.items?.map(item => item.name)?.join(', ')}{' '}
                    {cardPayment ? numberToCurrencyString(cardPayment.amount?.value ?? serviceCard.sale.remainingPrice?.vatIncluded, 'EUR', 0) : null}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>{getStatusMessage()}</Typography>
            </Grid>
            <Grid/>
            <Grid>
                {printReceipt === true &&
                    <PrintReceipt serviceCard={serviceCard} type="receipt" onPrintDone={onPrintDone} />
                }
            </Grid>
            <Grid className="footerButtons">
                <Grid container wrap="nowrap" spacing={4} justifyContent="space-between" alignItems="end">
                    <BackButton
                        disabled={fetching || (paymentInitialized && !cardPaymentCompleted && !cardPaymentCompletedStatus && !abortRequested)}
                        onBack={handleGoBack}
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}
